/**
 * Labels pt-BR dos tipos de notificação e dos canais de entrega.
 *
 * Mapa flat coerente com os labels da `NotificationsTab` (account) — cópia
 * intencional: a Tab agrupa por categoria com descrição por item, aqui é só o
 * rótulo curto usado na coluna "Tipo" e no filtro da página de log. Alinhar as
 * duas fontes num único catálogo é backlog registrado no spec.
 *
 * Conferido contra o enum real `NotificationType`
 * (`@troia-v3/types` → `dist/notifications.d.ts`) — os 48 tipos estão cobertos
 * (inclui os 6 de Checklists, adicionados junto com o módulo Checklists+Units).
 * Qualquer tipo novo cai no fallback de `getNotificationTypeLabel`.
 */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  // Sistema
  system_maintenance: 'Manutenção do Sistema',
  system_update: 'Atualizações do Sistema',
  system_alert: 'Alertas do Sistema',

  // Segurança
  auth_success: 'Login Bem-sucedido',
  auth_failure: 'Falha de Login',
  password_reset: 'Redefinição de Senha',
  two_factor_enabled: 'Autenticação de Dois Fatores',

  // Integrações
  integration_connected: 'Integração Conectada',
  integration_disconnected: 'Integração Desconectada',
  integration_error: 'Erro de Integração',

  // Agenda
  calendar_sync_complete: 'Sincronização de Agenda Concluída',
  calendar_sync_failed: 'Falha na Sincronização de Agenda',
  event_reminder: 'Lembrete de Evento',
  agenda_daily_summary: 'Resumo Diário da Agenda',

  // Canais
  channel_connected: 'Canal Conectado',
  channel_disconnected: 'Canal Desconectado',

  // Tickets
  ticket_assigned: 'Ticket Atribuído',
  ticket_status_changed: 'Status do Ticket Alterado',
  ticket_comment_added: 'Novo Comentário no Ticket',
  sla_warning: 'SLA perto do vencimento',
  sla_breach: 'SLA violado',

  // Conversas
  conversation_assigned: 'Conversa Atribuída',
  conversation_message_received: 'Nova Mensagem',

  // Leads
  lead_assigned: 'Lead Atribuído',
  lead_status_changed: 'Status do Lead Alterado',
  lead_activity_assigned: 'Atividade de Lead Atribuída',
  lead_alert_warning: 'Lead em Alerta',
  lead_alert_critical: 'Lead Crítico',
  lead_weekly_digest: 'Resumo Semanal de Leads Parados',

  // Projetos
  project_task_unblocked: 'Dependência Liberada',
  project_task_completed: 'Tarefa de Projeto Concluída',
  project_task_overdue: 'Tarefa de Projeto Atrasada',
  project_completed: 'Projeto Concluído',

  // Escalonamento
  escalation_triggered: 'Escalonamento Acionado',
  escalation_reassigned: 'Escalonamento Reatribuído',

  // Pagamentos
  payment_received: 'Pagamento Recebido',
  payment_failed: 'Falha no Pagamento',
  subscription_renewed: 'Assinatura Renovada',
  subscription_expired: 'Assinatura Expirada',
  subscription_canceled: 'Assinatura Cancelada',
  credit_usage_alert: 'Créditos Acabando',

  // Equipe
  team_member_added: 'Membro Adicionado à Equipe',
  team_member_removed: 'Membro Removido da Equipe',
  shift_reminder: 'Lembrete de Turno',

  // Checklists
  checklist_assigned: 'Checklist atribuído',
  checklist_due_soon: 'Checklist perto de expirar',
  checklist_expired: 'Checklist expirado',
  checklist_completed: 'Checklist enviado para revisão',
  checklist_approved: 'Checklist aprovado',
  checklist_rejected: 'Checklist reprovado',

  // Automação (2026-08-22)
  workflow_auto_paused: 'Workflow pausado automaticamente',
  workflow_frequent_failures: 'Workflow falhando com frequência',

  // Campanhas (2026-08-27)
  campaign_auto_paused: 'Campanha pausada automaticamente',

  // Custom
  custom_notification: 'Notificação Personalizada',
};

/** Rótulo pt-BR do tipo — fallback legível para tipos ainda não catalogados. */
export const getNotificationTypeLabel = (type: string): string =>
  NOTIFICATION_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');

/** Rótulos dos canais de entrega (`NotificationChannel`). */
export const CHANNEL_LABELS: Record<string, string> = {
  inApp: 'Sino',
  push: 'Push',
  email: 'Email',
  whatsapp: 'WhatsApp',
  sound: 'Som',
};

/** Rótulo do canal — fallback no próprio valor para canais novos. */
export const getChannelLabel = (channel: string): string => CHANNEL_LABELS[channel] ?? channel;
